import {Edge, GetPolygonBounds, IsPointInPolygon, OptimizePolygon, TraceEdgesToPolygon} from "./PolygonUtils.js";
import type {Vec2, Vec2Simple} from "./VectorStructs.js";

export function GridCellsToPolygon(cellPositions: Vec2Simple[], cellSize: number, optimize = true) {
	const cellPositions_set = new Set(cellPositions.map(([x, y])=>`${x},${y}`));
	// Convert string keys to grid coordinates
	const cells = Array.from(cellPositions).map(key=>{
		//const [x, y] = key.split(',').map(Number);
		const [x, y] = key;
		return {x, y};
	});

	// Find boundary edges
	const edges = new Map<string, Edge>();

	for (const cell of cells) {
		// Check 4 neighbors
		const neighbors = [
			{dx: 1, dy: 0, edge: "right"},
			{dx: 0, dy: 1, edge: "bottom"},
			{dx: -1, dy: 0, edge: "left"},
			{dx: 0, dy: -1, edge: "top"}
		];

		for (const {dx, dy, edge} of neighbors) {
			const nx = cell.x + dx;
			const ny = cell.y + dy;
			const neighborKey = `${nx},${ny}`;

			// If neighbor is not selected, this edge is on the boundary
			if (!cellPositions_set.has(neighborKey)) {
				// Calculate edge vertices
				let x1, y1, x2, y2;

				if (edge === "right") {
					x1 = (cell.x + 1) * cellSize;
					y1 = cell.y * cellSize;
					x2 = (cell.x + 1) * cellSize;
					y2 = (cell.y + 1) * cellSize;
				} else if (edge === "bottom") {
					x1 = cell.x * cellSize;
					y1 = (cell.y + 1) * cellSize;
					x2 = (cell.x + 1) * cellSize;
					y2 = (cell.y + 1) * cellSize;
				} else if (edge === "left") {
					x1 = cell.x * cellSize;
					y1 = cell.y * cellSize;
					x2 = cell.x * cellSize;
					y2 = (cell.y + 1) * cellSize;
				} else if (edge === "top") {
					x1 = cell.x * cellSize;
					y1 = cell.y * cellSize;
					x2 = (cell.x + 1) * cellSize;
					y2 = cell.y * cellSize;
				}

				// Create a unique key for this edge
				const edgeKey = `${Math.min(x1, x2)},${Math.min(y1, y2)}-${Math.max(x1, x2)},${Math.max(y1, y2)}`;
				edges.set(edgeKey, {start: [x1, y1], end: [x2, y2]});
			}
		}
	}

	// Now trace around the boundary to form a polygon
	const edgeList = Array.from(edges.values());
	let result = TraceEdgesToPolygon(edgeList);
	if (optimize) result = OptimizePolygon(result);
	return result;
}

export function PolygonToGridCells(points_mobx: Vec2Simple[], cellSize: number) {
	const points = points_mobx.map(a=>[a[0], a[1]]) as [number, number][]; // convert to simple array (mobx array has slight overhead when accessing entries)

	const cells = [] as Vec2Simple[];
	if (points.length < 3) return cells;

	// Find bounding box of polygon
	const bounds = GetPolygonBounds(points);

	// Convert to cell coordinates and add a small margin to ensure we catch edge cases
	/*const minCellX = Math.floor(bounds.minX / cellSize) - 1;
	const maxCellX = Math.ceil(bounds.maxX / cellSize) + 1;*/
	const minCellY = Math.floor(bounds.minY / cellSize) - 1;
	const maxCellY = Math.ceil(bounds.maxY / cellSize) + 1;

	// Use a scanline algorithm (more efficient than checking every cell)
	for (let cellY = minCellY; cellY <= maxCellY; cellY++) {
		const y = cellY * cellSize + cellSize / 2; // Cell center y-coordinate

		// Find x-intercepts with polygon edges
		const intercepts: number[] = [];
		for (let i = 0; i < points.length; i++) {
			const j = (i + 1) % points.length;
			const [x1, y1] = points[i];
			const [x2, y2] = points[j];

			// Skip horizontal edges, and edges not crossing this scanline.
			const yLo = Math.min(y1, y2), yHi = Math.max(y1, y2);
			// Use the half-open interval rule [y1, y2) so that a vertex shared by two edges (eg. due to fractional polygon points) is counted exactly once.
			// This prevents duplicate intercepts that would pair up wrongly, and miss many cells in a row. (eg. as on 2026-04-20, prompting this fix)
			if (y1 === y2 || y < yLo || y >= yHi) continue;

			// Calculate x-intercept
			const x = x1 + (y - y1) * (x2 - x1) / (y2 - y1);
			intercepts.push(x);
		}

		// Sort intercepts
		intercepts.sort((a, b)=>a - b);

		// Add cells between pairs of intercepts
		for (let i = 0; i < intercepts.length; i += 2) {
			if (i + 1 >= intercepts.length) break;

			const startCellX = Math.ceil(intercepts[i] / cellSize - 0.5);
			const endCellX = Math.floor(intercepts[i+1] / cellSize - 0.5);

			for (let cellX = startCellX; cellX <= endCellX; cellX++) {
				cells.push([cellX, cellY]);
			}
		}
	}

	return cells;
}